import { useEffect, useState } from "react"
import useFetch from "../../../_fetch"
import { Col, ListGroup, Row, Form, Accordion, Button, Spinner, Alert } from "react-bootstrap"
import { NetworkForm } from "./network-form"
import { FaCheckCircle, FaCircleNotch } from "react-icons/fa"
import { NetworkTable } from "./network-table"
import { getColor } from "../../../_utils"
import { useAuth } from "../../../auth/auth-context"

export type INetworkIntegration = {
    name: string
    fields: ({ name: string; secret?: boolean, displayName?: string } | { name: string; multiple: boolean; attributes: string[]; secret?: boolean, displayName?: string, displayAttributes?: string[] })[]
    values: { [key: string]: string | { [key: string]: string }[] }[]
    description?: string
}

export type INetworkSettings = {
    networkIntegrations: INetworkIntegration[],
    userSettings: { webhookURL?: string }
}

export default function Settings() {
    const { doFetch } = useFetch()
    const { user } = useAuth()
    const isPublisher = user?.kind === "publisher"
    const [networkData, setNetworkData] = useState<INetworkIntegration[]>([])
    const [activeNetwork, setActiveNetwork] = useState<INetworkIntegration>()
    const [callbackUrl, setCallbackUrl] = useState("")
    const [isUrlValid, setIsUrlValid] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")

    useEffect(() => {
        fetchUserData()
    }, [])

    const fetchUserData = async () => {
        try {
            const res = await doFetch("/network-settings")
            const nwSettings = (await res.json()) as INetworkSettings
            nwSettings.networkIntegrations.sort((a, b) => a.name.localeCompare(b.name))
            
            const nws = nwSettings.networkIntegrations
            setCallbackUrl(nwSettings.userSettings.webhookURL || "")
            setNetworkData(nws)
            if (activeNetwork) {
                const nw = nws.find((nw) => nw.name === activeNetwork.name)
                setActiveNetwork(nw)
            }
        } catch (error) {
            console.error("Error fetching user data:", error)
        }
    }

    const isValidUrl = (url: string): boolean => {
        if (url === '') return true
        try {
            const parsedUrl = new URL(url)
            return ['http:', 'https:'].includes(parsedUrl.protocol)
        } catch (err) {
            return false
        }
    }

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const url = e.target.value
        setCallbackUrl(url)
        setIsUrlValid(isValidUrl(url))
    }

    const saveSettings = async () => {
        if (!isUrlValid) {
            alert("Please enter a valid URL.")
            return
        }
        
        setIsSaving(true) 
        await new Promise((r)=>setTimeout(r,500))

        try {
            const updatedData = { webhookURL: callbackUrl }
            const res = await doFetch('/network/update-user-settings', { settings: updatedData })
            if (res.status >= 400) throw new Error("Error saving settings.")
        } catch (error) {
            console.error("Error saving settings:", error)
            alert("Error saving settings.")
        } finally {
            setIsSaving(false) 
        }
    }

    // Tjek om alle network values er tomme
    const allNetworksEmpty = networkData.length > 0 && networkData.every(nw => nw.values.length === 0)

    return (
        <div className="mt-3 ms-3">
            <Row className="me-0">
                <Col sm={12}>
                    <Accordion defaultActiveKey="1">
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>🌐 Networks</Accordion.Header>
                            <Accordion.Body>
                                <Row>
                                    <Col sm={12}>
                                        {/* Kombineret alert med gul baggrund */}
                                        {(allNetworksEmpty || networkData.length > 0) && (
                                            <Alert variant="warning" role="alert">
                                                {allNetworksEmpty && (
                                                    <div className="mb-2">
                                                        <strong>Notice:</strong> The dashboard functionalities will only become available once you have configured one or more networks. Once network credentials are set up, you will gain full access to dashboard insights and data-driven reporting.
                                                    </div>
                                                )}
                                                <div className="mb-2">
                                                    <strong>Heads up:</strong> After adding your credentials, it may take up to <strong>30 minutes</strong> for data to become visible, as up to <strong>2 years of historical data</strong> is being retrieved.
                                                </div>
                                                <div>
                                                    If you are unsure how to set up a campaign or obtain network credentials, a guide is available <a href="https://nordpar.com/affiliate-marketing-api/" target="_blank" rel="noopener noreferrer">here</a>.
                                                </div>
                                            </Alert>
                                        )}
                                    </Col>
                                    <Col sm={3}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search networks..."
                                            className="mb-2"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                                            <ListGroup>
                                                {networkData
                                                    .filter(nw => nw.name.toLowerCase().includes(searchTerm.toLowerCase()))
                                                    .map((nw, i) => (
                                                        <a
                                                            key={nw.name}
                                                            href="#"
                                                            className={`list-group-item list-group-item-action ${nw.name === activeNetwork?.name ? "active" : ""}`}
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                setActiveNetwork(nw)
                                                            }}
                                                        >
                                                            <h5>
                                                                <span className="small me-1" style={{ color: getColor(nw.name, i) }}>
                                                                    ●
                                                                </span>
                                                                {nw.name}
                                                            </h5>
                                                            <div className="d-flex align-items-center">
                                                                <span>
                                                                    {nw.values.length ? `${nw.values.length} validated credentials` : "Ready to be set up"}
                                                                </span>
                                                                <span className="ms-auto">
                                                                    {nw.values.length ? <FaCheckCircle style={{ marginRight: "5px" }} /> : <FaCircleNotch style={{ marginRight: "5px" }} />}
                                                                </span>
                                                            </div>
                                                        </a>
                                                ))}
                                            </ListGroup>
                                        </div>
                                    </Col>
                                    {activeNetwork && (
                                        <>
                                        <Col sm={9}>
                                            <Row>
                                                <Col sm={5}>
                                                    <NetworkForm network={activeNetwork} fetchUserData={fetchUserData} />
                                                </Col>
                                                <Col sm={7}>
                                                    <NetworkTable network={activeNetwork} fetchUserData={fetchUserData} />
                                                </Col>
                                                <Col sm={12}>
                                                    {activeNetwork.description && (
                                                        <Alert variant="light" className="mt-2">
                                                            <div dangerouslySetInnerHTML={{ __html: activeNetwork.description }} />
                                                        </Alert>
                                                    )}
                                                </Col>
                                            </Row>
                                        </Col>
                                        </>
                                    )}
                                </Row>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    <Accordion className="my-3">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>⚙️ Global Settings</Accordion.Header>
                            <Accordion.Body>
                                <Form.Group controlId="callbackUrl">
                                    <Form.Label>Custom Webhook URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter your webhook URL"
                                        value={callbackUrl}
                                        onChange={handleUrlChange}
                                        isInvalid={!isUrlValid && callbackUrl !== ""}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Please enter a valid URL.
                                    </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            This URL will be called when a sale is registered.  
                                            You can include the following placeholders, which will be replaced with actual values:<br />
                                            <div className="fw-bold mb-1">
                                                <code>{'{id}'}</code>,<code>{'{commission}'}</code>,<code>{'{date}'}</code>,<code>{'{country}'}</code>,<code>{'{network}'}</code>,  
                                                <code>{'{clickReference}'}</code>,<code>{'{campaignId}'}</code>,<code>{'{campaignName}'}</code>,
                                                {isPublisher ? '' : <>
                                                    <code>{'{affiliateId}'}</code>,<code>{'{affiliateName}'}</code>,<code>{'{orderAmount}'}</code> ,<code>{'{orderReference}'}</code>
                                                </>}
                                            </div>

                                            <div className="mb-2"><strong>Note:</strong> All currency values are shown in your selected currency: <strong>{user?.settings?.currency}</strong>.</div>

                                            Example:
                                            <code className="mt-2"> https://mydomain.org/?id={'{id}'}&commission={'{commission}'}&network={'{network}'}&value={'{orderAmount}'}</code>
                                        </Form.Text>
                                </Form.Group>

                                <Button
                                    variant="primary"
                                    onClick={saveSettings}
                                    className="mt-3"
                                    disabled={!isUrlValid || isSaving}
                                >
                                    {isSaving ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                            <span className="ms-1">Saving...</span>
                                        </>
                                    ) : (
                                        "Save Settings"
                                    )}
                                </Button>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Col>
            </Row>
        </div>
    )
}
